/**
 * Platform > Agents (all users).
 *
 * Admin-only cross-workspace view of every agent. Mirrors the Approvals
 * table pattern. Admins can emergency-pause an agent (and resume it) and
 * revoke any of its keys. Gated server-side by requirePlatformAdmin — the
 * Settings nav also hides this entry for non-admins.
 */

import { useState } from 'react';
import {
  useAdminAgents,
  useAdminUpdateAgent,
  useAdminAgentKeys,
  useAdminRevokeKey,
  useAdminIssueKey,
  type AdminAgentDto,
  type AgentKeyDto,
  type AgentStatus,
} from '../../api/agents';
import { FormModal } from '../../components/FormModal';
import { TokenReveal } from '../../components/TokenReveal';
import styles from './PlatformAgents.module.css';

const STATUS_FILTERS: { value: AgentStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'archived', label: 'Archived' },
];

export function PlatformAgentsPage() {
  const [status, setStatus] = useState<AgentStatus | ''>('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [userId, setUserId] = useState('');

  const query = useAdminAgents({
    status: status || undefined,
    workspaceId: workspaceId.trim() || undefined,
    userId: userId.trim() || undefined,
  });
  const agents = query.data?.agents ?? [];

  const [openKeysFor, setOpenKeysFor] = useState<string | null>(null);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Agents (all users)</h1>
        <p className={styles.sub}>
          Every agent across all workspaces. Pause an agent to suspend it, or
          revoke a specific key. Workspace members manage their own agents under
          Profile → My agents.
        </p>
      </header>

      <div className={styles.filters}>
        <div className={styles.pills}>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.label}
              type="button"
              className={`${styles.pill} ${status === f.value ? styles.pillActive : ''}`.trim()}
              onClick={() => setStatus(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          className={styles.filterInput}
          placeholder="Filter by workspace ID"
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
        />
        <input
          className={styles.filterInput}
          placeholder="Filter by owner user ID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
      </div>

      {query.isLoading && <div className={styles.empty}>Loading…</div>}
      {query.isError && (
        <div className={styles.empty}>
          Couldn’t load agents. {(query.error as Error)?.message ?? ''}
        </div>
      )}
      {!query.isLoading && !query.isError && agents.length === 0 && (
        <div className={styles.empty}>No agents match these filters.</div>
      )}

      {agents.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Label</th>
                <th>Workspace</th>
                <th>Owner</th>
                <th>Status</th>
                <th className={styles.numCell}>Keys</th>
                <th className={styles.actionsCell} />
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <AgentRow
                  key={a.id}
                  agent={a}
                  keysOpen={openKeysFor === a.id}
                  onToggleKeys={() =>
                    setOpenKeysFor((cur) => (cur === a.id ? null : a.id))
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AgentRow({
  agent,
  keysOpen,
  onToggleKeys,
}: {
  agent: AdminAgentDto;
  keysOpen: boolean;
  onToggleKeys: () => void;
}) {
  const update = useAdminUpdateAgent();
  const paused = agent.status === 'paused';

  const togglePause = () => {
    update.mutate({
      id: agent.id,
      patch: { status: paused ? 'active' : 'paused' },
    });
  };

  return (
    <>
      <tr>
        <td>{agent.label}</td>
        <td className={styles.muted}>{agent.workspaceName}</td>
        <td className={styles.muted}>{agent.ownerEmail ?? '—'}</td>
        <td>
          <span className={`${styles.badge} ${styles[`badge_${agent.status}`]}`}>
            {agent.status}
          </span>
        </td>
        <td className={styles.numCell}>
          {agent.keyCount > 0 ? (
            <button type="button" className={styles.linkBtn} onClick={onToggleKeys}>
              {agent.keyCount} {keysOpen ? '▾' : '▸'}
            </button>
          ) : (
            <span className={styles.muted}>0</span>
          )}
        </td>
        <td className={styles.actionsCell}>
          {agent.status !== 'archived' && (
            <button
              type="button"
              className={paused ? 'kos-btn kos-btn-primary' : styles.btnWarnGhost}
              onClick={togglePause}
              disabled={update.isPending}
            >
              {paused ? 'Resume' : 'Pause'}
            </button>
          )}
        </td>
      </tr>
      {keysOpen && (
        <tr>
          <td colSpan={6} className={styles.keysCell}>
            <AdminKeys agentId={agent.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function AdminKeys({ agentId }: { agentId: string }) {
  const keysQ = useAdminAgentKeys(agentId);
  const revoke = useAdminRevokeKey();
  const keys = keysQ.data?.keys ?? [];
  const [regenKey, setRegenKey] = useState<AgentKeyDto | null>(null);

  if (keysQ.isLoading) return <div className={styles.keysEmpty}>Loading keys…</div>;
  if (keys.length === 0) return <div className={styles.keysEmpty}>No active keys.</div>;

  return (
    <>
      <table className={styles.keysTable}>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id}>
              <td>{k.label}</td>
              <td className={styles.muted}>
                {k.appScope === null || k.appScope.length === 0
                  ? 'All apps'
                  : k.appScope.join(', ')}
              </td>
              <td className={styles.muted}>
                {k.lastUsedAt
                  ? new Date(k.lastUsedAt).toLocaleDateString(undefined, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : 'never used'}
              </td>
              <td className={styles.actionsCell}>
                <button
                  type="button"
                  className="kos-btn"
                  onClick={() => setRegenKey(k)}
                  disabled={revoke.isPending || regenKey !== null}
                >
                  Regenerate
                </button>
                <button
                  type="button"
                  className={styles.btnDangerGhost}
                  onClick={() => revoke.mutate({ agentId, keyId: k.id })}
                  disabled={revoke.isPending || regenKey?.id === k.id}
                >
                  Revoke
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {regenKey && (
        <AdminRegenerateKeyModal
          agentId={agentId}
          keyRow={regenKey}
          onClose={() => setRegenKey(null)}
        />
      )}
    </>
  );
}

// ─── Admin regenerate (revoke-first, then re-issue; raw token shown once) ──

function AdminRegenerateKeyModal({
  agentId,
  keyRow,
  onClose,
}: {
  agentId: string;
  keyRow: AgentKeyDto;
  onClose: () => void;
}) {
  const revoke = useAdminRevokeKey();
  const issue = useAdminIssueKey();
  const [rawToken, setRawToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pending = revoke.isPending || issue.isPending;

  const regenerate = async () => {
    if (pending) return;
    setError(null);
    try {
      await revoke.mutateAsync({ agentId, keyId: keyRow.id });
    } catch {
      setError('Could not revoke the current token. Nothing changed — try again.');
      return;
    }
    try {
      const res = await issue.mutateAsync({
        agentId,
        label: keyRow.label,
        appScope: keyRow.appScope ?? undefined,
      });
      setRawToken(res.key);
    } catch {
      setError(
        'The old token was revoked, but issuing a new one failed. Close this and issue a new key manually.',
      );
    }
  };

  if (rawToken) {
    return (
      <FormModal
        open
        onClose={onClose}
        title="Token regenerated"
        subtitle="Copy the new token now — it is shown only once. The previous token no longer works."
        submitLabel="Done"
        onSubmit={onClose}
      >
        <TokenReveal token={rawToken} />
      </FormModal>
    );
  }

  return (
    <FormModal
      open
      onClose={pending ? () => {} : onClose}
      title="Regenerate token"
      subtitle={`This revokes "${keyRow.label}" immediately and issues a replacement with the same scope. The agent using the old token will stop working until it is updated.`}
      submitLabel={pending ? 'Regenerating…' : 'Regenerate'}
      submitDisabled={pending}
      onSubmit={regenerate}
      error={error}
    >
      <p className={styles.sub}>
        You are regenerating a key on behalf of its owner. The new token is
        shown to you once on the next screen.
      </p>
    </FormModal>
  );
}
